/**
 * WebSocketClientTransport — connects to a skaile serve WebSocket server.
 *
 * Handles connection, reconnection with exponential backoff, heartbeat,
 * and JSON serialization. Implements ClientTransport from @skaile/workspaces/types.
 */

import type { AgentCommand, AgentEvent, ClientTransport } from "@skaile/workspaces/types";
import { encodeBearerSubprotocol } from "./auth.js";

const DEFAULT_RECONNECT_MAX_ATTEMPTS = 5;
const DEFAULT_RECONNECT_BASE_MS = 1_000;
const DEFAULT_RECONNECT_MAX_MS = 30_000;
const DEFAULT_HEARTBEAT_MS = 30_000;
const DEFAULT_CONNECT_TIMEOUT_MS = 10_000;

/**
 * Options for {@link WebSocketClientTransport}.
 *
 * @param url - WebSocket server URL to connect to (e.g. `ws://localhost:8080`).
 * @param reconnect - Exponential-backoff reconnect policy. Defaults: 5 attempts, 1s base, 30s max.
 * @param heartbeatMs - Interval in milliseconds between WebSocket ping frames. Default: 30 000.
 * @param connectTimeoutMs - Maximum time in milliseconds to wait for the initial handshake. Default: 10 000.
 * @param onLog - Optional callback for internal log lines (connection state, errors).
 * @param auth - Optional bearer token sent via the WebSocket subprotocol; must
 *   match the server's `authToken` or the handshake is rejected with HTTP 401.
 *
 * @docLink packages/transport/dev-guide#websocket-client-transport
 */
export interface WebSocketClientOptions {
  url: string;
  reconnect?: {
    maxAttempts?: number;
    baseMs?: number;
    maxMs?: number;
  };
  heartbeatMs?: number;
  connectTimeoutMs?: number;
  onLog?: (line: string) => void;
  auth?: string;
}

/**
 * `ClientTransport` implementation that connects to a skaile agent server over WebSocket.
 *
 * Uses the browser-standard `WebSocket` API so it works in both Bun and browser
 * environments. On an unintentional disconnect it automatically retries with
 * exponential backoff (default: 5 attempts, 1s base, 30s max). A periodic ping
 * keeps the connection alive through NAT and load-balancer idle timeouts. All
 * `AgentCommand` / `AgentEvent` messages are JSON-serialised on the wire.
 *
 * @example
 * ```ts
 * const transport = new WebSocketClientTransport({ url: 'ws://localhost:8080' });
 * await transport.connect();
 * transport.send({ type: 'prompt', prompt: 'Hello' });
 * ```
 *
 * @docLink packages/transport/dev-guide#websocket-client-transport
 */
export class WebSocketClientTransport implements ClientTransport {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private readonly reconnectConfig: Required<NonNullable<WebSocketClientOptions["reconnect"]>>;
  private readonly heartbeatMs: number;
  private readonly connectTimeoutMs: number;
  private readonly log: (line: string) => void;
  private readonly subprotocols: string[];

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;

  private readonly messageHandlers = new Set<(event: AgentEvent) => void>();
  private readonly disconnectHandlers = new Set<() => void>();

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.reconnectConfig = {
      maxAttempts: options.reconnect?.maxAttempts ?? DEFAULT_RECONNECT_MAX_ATTEMPTS,
      baseMs: options.reconnect?.baseMs ?? DEFAULT_RECONNECT_BASE_MS,
      maxMs: options.reconnect?.maxMs ?? DEFAULT_RECONNECT_MAX_MS,
    };
    this.heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
    this.connectTimeoutMs = options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;
    this.log = options.onLog ?? (() => {});
    this.subprotocols = options.auth ? [encodeBearerSubprotocol(options.auth)] : [];
  }

  async connect(): Promise<void> {
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    return this.doConnect(true);
  }

  async disconnect(): Promise<void> {
    this.intentionalClose = true;
    this.clearHeartbeat();
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, "Client disconnect");
      }
      this.ws = null;
    }
  }

  send(command: AgentCommand): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log("[ws-client] cannot send: not connected");
      return;
    }
    this.ws.send(JSON.stringify(command));
  }

  onMessage(handler: (event: AgentEvent) => void): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  onDisconnect(handler: () => void): () => void {
    this.disconnectHandlers.add(handler);
    return () => {
      this.disconnectHandlers.delete(handler);
    };
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private doConnect(isInitial = false): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.log(`[ws-client] connecting to ${this.url}`);

      // Pass the bearer subprotocol when auth is configured; an empty list keeps
      // the no-auth handshake byte-identical to before.
      const ws =
        this.subprotocols.length > 0
          ? new WebSocket(this.url, this.subprotocols)
          : new WebSocket(this.url);
      this.ws = ws;

      // Settle the connect promise exactly once. A close or error that arrives
      // before `open` is a handshake/connect failure (e.g. a rejected auth
      // upgrade, code 401, or a refused port) — reject so the caller sees it
      // instead of hanging until the connect timeout.
      let settled = false;
      let opened = false;
      const failConnect = (message: string) => {
        if (settled) return;
        settled = true;
        reject(new Error(message));
      };

      const timeout = setTimeout(() => {
        if (settled) return;
        // Reject with the timeout reason first, then close — closing may
        // synchronously fire the `close` handler, whose `failConnect` is then a
        // no-op thanks to the `settled` guard (preserves the timeout message).
        settled = true;
        reject(new Error(`Connection timeout to ${this.url}`));
        ws.close();
      }, this.connectTimeoutMs);

      ws.addEventListener("open", () => {
        clearTimeout(timeout);
        opened = true;
        this.log("[ws-client] connected");
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        if (!settled) {
          settled = true;
          resolve();
        }
      });

      ws.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(String(event.data)) as AgentEvent;
          for (const handler of this.messageHandlers) {
            handler(data);
          }
        } catch {
          this.log("[ws-client] failed to parse message");
        }
      });

      ws.addEventListener("close", (event) => {
        clearTimeout(timeout);
        this.clearHeartbeat();
        this.log(`[ws-client] disconnected (code=${event.code})`);

        for (const handler of this.disconnectHandlers) {
          handler();
        }

        // Closed before ever opening == connect failure. Reject the pending
        // connect; only background (reconnect) attempts chain another retry —
        // an initial connect failure is surfaced to the caller, who owns retry.
        if (!opened) {
          failConnect(`Connection to ${this.url} closed before open (code=${event.code})`);
          if (!isInitial && !this.intentionalClose && event.code !== 1000) {
            this.scheduleReconnect();
          }
          return;
        }

        if (!this.intentionalClose && event.code !== 1000) {
          this.scheduleReconnect();
        }
      });

      ws.addEventListener("error", () => {
        clearTimeout(timeout);
        // A pre-open error is a connect failure; reject now. The close event
        // (which usually follows) is idempotent via the `settled` guard.
        if (!opened) failConnect(`Connection to ${this.url} failed before open`);
      });
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.reconnectConfig.maxAttempts) {
      this.log(`[ws-client] giving up reconnect after ${this.reconnectAttempts} attempts`);
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectConfig.baseMs * 2 ** (this.reconnectAttempts - 1),
      this.reconnectConfig.maxMs,
    );
    this.log(`[ws-client] reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.intentionalClose) return;
      this.doConnect().catch(() => {
        // doConnect rejection triggers another close → scheduleReconnect cycle
      });
    }, delay);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // WebSocket ping — Bun's WebSocket supports ping natively
        try {
          (this.ws as unknown as { ping?: () => void }).ping?.();
        } catch {
          // Fallback: some environments don't support ping on the client
        }
      }
    }, this.heartbeatMs);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}
